import { createResource, createMemo, type Accessor } from 'solid-js';
import { isServer } from 'solid-js/web';
import type { LayoutCalc, Box, Dimensions, Position } from '../../types';

// ── Shared geometry types ───────────────────────────────────────────────────

export type ImgAttrs = { box: Box; ar: 'none' | 'xMidYMid slice' };

// ── Pure helpers ──────────────────────────────────────────────────────────

// Load an image purely to read its natural dimensions. Resolves null on error
// (or when there is no usable source) so the caller can render the placeholder.
export function loadNaturalSize(s: string): Promise<Dimensions | null> {
	return new Promise(resolve => {
		const img = new Image()
		img.onload  = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
		img.onerror = () => resolve(null)
		img.src     = s
	})
}

// Cover-fit the natural image into the viewport, then apply zoom. Returns the
// drawn rect plus the max pan offsets that keep the image covering the viewport.
export function computeLayout(nat: Dimensions, vw: number, vh: number, z: number, r: number): LayoutCalc {
	const effectiveW = nat.width * r
	const scale = Math.max(vw / effectiveW, vh / nat.height) * z
	const iw = effectiveW * scale
	const ih = nat.height * scale
	return {
		box: { pos: { x: (vw - iw) / 2, y: (vh - ih) / 2 }, dims: { width: iw, height: ih } },
		maxPanX: Math.max(0, (iw - vw) / 2),
		maxPanY: Math.max(0, (ih - vh) / 2),
	}
}

// ── Reactive builders ─────────────────────────────────────────────────────

// Load the displayed image's natural size. The fetcher only runs for non-null
// sources and stale Promises are discarded automatically when the source
// changes. resetView() runs at the START of each fetch (before the image loads)
// so zoom/stretch/pan reset as soon as the new src is known. The last known size
// is kept while a new image loads to avoid flicker; null when there's no src so
// the placeholder renders.
//
// An unresolved first load reports null rather than reading `latest`: on an unresolved
// resource `latest` falls through to a tracked read, which registers with the enclosing
// <Suspense> and detaches the whole page (losing its scroll position) while the image
// loads. Once resolved, `latest` returns the previous size directly and never suspends.
export function createNaturalSize(effectiveSrc: Accessor<string | null>, resetView: () => void): Accessor<Dimensions | null> {
	// There is no Image to load on the server, and renderToStringAsync awaits every resource — a
	// fetcher that threw there would reject the whole page render. Natural size only feeds the
	// browser's crop/cover layout, so the resource simply never starts in the SSR pass.
	const [res] = createResource(() => isServer ? undefined : effectiveSrc(), (s): Promise<Dimensions | null> => {
		resetView()
		return loadNaturalSize(s)
	})
	return () => {
		if (!effectiveSrc()) return null
		if (res.state === 'pending') return null
		return res.latest ?? null
	}
}

// Current view state the geometry memos read from. `frame` is the rendered box size, supplied
// by the host (DraggableElement) via its children render-prop — geometry needs only the size,
// not the position.
export interface ImageViewState {
	natSize: Accessor<Dimensions | null>;
	frame:   Accessor<Dimensions>;
	zoom:    Accessor<number>;
	stretch: Accessor<number>;
	pan:     Accessor<Position>;
}

export interface ImageGeometry {
	layout:   Accessor<LayoutCalc | null>;
	cPan:     Accessor<Position>;
	imgAttrs: Accessor<ImgAttrs>;
}

export function createImageGeometry(view: ImageViewState): ImageGeometry {
	const { natSize, frame, zoom, stretch, pan } = view

	const layout = createMemo((): LayoutCalc | null => {
		const nat = natSize()
		if (!nat || !nat.width || !nat.height) return null
		const { width, height } = frame()
		return computeLayout(nat, width, height, zoom(), stretch())
	})

	// Clamp the requested pan to the layout's max offsets so the image keeps
	// covering the viewport.
	const cPan = createMemo((): Position => {
		const l = layout()
		const p = pan()
		if (!l) return { x: 0, y: 0 }
		return {
			x: Math.max(-l.maxPanX, Math.min(l.maxPanX, p.x)),
			y: Math.max(-l.maxPanY, Math.min(l.maxPanY, p.y)),
		}
	})

	// Final <image> attributes: the cover-fit rect when sized, else a plain
	// slice-fit fallback before the natural size is known.
	const imgAttrs = createMemo((): ImgAttrs => {
		const l = layout()
		const p = cPan()
		if (l) return { box: { pos: { x: l.box.pos.x + p.x, y: l.box.pos.y + p.y }, dims: l.box.dims }, ar: 'none' }
		const { width, height } = frame()
		return { box: { pos: { x: 0, y: 0 }, dims: { width, height } }, ar: 'xMidYMid slice' }
	})

	return { layout, cPan, imgAttrs }
}
